/**
 * Theme state: explicit toggle, persisted, falling back to OS preference.
 *
 * Lifted from plugins/augurly_admin/svelte/src/lib/theme.svelte.js rather than
 * plugins/augurly's, which is prefers-color-scheme only with no way for a user
 * to override it.
 *
 * The $state object is exported directly (not wrapped in a class) because that
 * is what makes it reactive across module boundaries. Note $effect() cannot be
 * used inside a .svelte.js module.
 */

const STORAGE_KEY = 'strao-portal-theme';

function stored() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function osPrefersDark() {
  return typeof window !== 'undefined'
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function initial() {
  return stored() || (osPrefersDark() ? 'dark' : 'light');
}

export const themeState = $state({ current: initial() });

function apply(theme) {
  themeState.current = theme;
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

export const themeActions = {
  set(theme) {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch { /* private browsing — the in-memory theme still applies */ }
    apply(theme);
  },
  toggle() {
    themeActions.set(themeState.current === 'dark' ? 'light' : 'dark');
  },
  resetToOSPreference() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
    apply(osPrefersDark() ? 'dark' : 'light');
  },
};

/**
 * Applied before mount, so the app shell never paints in the wrong theme.
 * The Jinja template runs an equivalent inline snippet in <head> to cover the
 * gap before this bundle has even parsed.
 */
export function initTheme() {
  apply(themeState.current);

  if (typeof window !== 'undefined') {
    window
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', (e) => {
        // Only follow the OS while the user has expressed no preference.
        if (!stored()) apply(e.matches ? 'dark' : 'light');
      });
  }
}
